import React, { useState, useEffect } from 'react'
import Layout from '../component/layout'
import { useNavigate } from 'react-router-dom'
import { toastify } from '../../../utils/toast'
import { makeRequest2 } from '../../../utils/makeRequest'
import { addNewValueAddedService, getValueAddedServices } from '../../../utils/urls'
import { getItem } from '../../../utils/localStorage'

const ServiceVendor = () => {

    const navigate = useNavigate()
    const [activeTab, setActiveTab] = useState('add'); // 'add' or 'list'

    // Form states
    const [serviceName, setServiceName] = useState("");
    const [providerType, setProviderType] = useState("INTERNAL");

    // Services list states
    const [services, setServices] = useState([]);
    const [loading, setLoading] = useState(false);
    const [fetchingServices, setFetchingServices] = useState(false);

    const handleTabClick = (tabId) => {
        setActiveTab(tabId);
        if (tabId === 'list') {
            fetchServices();
        }
    };

    const fetchServices = async () => {
        setFetchingServices(true);
        try {
            const facilityId = await getItem('selectedFacilityId');
            const response = await makeRequest2(`${getValueAddedServices}/${facilityId}`, 'GET');

            console.log("fetchServices response:", response);

            if (response.success) {
                const services = response.data?.services || [];
                const formattedServices = services.map(service => ({
                    ...service,
                    label: service.serviceName,
                    value: service._id
                }));
                setServices(formattedServices);
            } else {
                setServices([]);
                toastify(response.message, 'error');
            }
        } catch (error) {
            console.error('Error fetching services:', error);
            setServices([]);
            toastify(error.message, 'error');
        } finally {
            setFetchingServices(false);
        }
    };

    const handleAddService = async (e) => {
        e.preventDefault();

        // Validation
        if (!serviceName.trim()) {
            toastify('Please enter a service name', 'error');
            return;
        }

        setLoading(true);

        try {
            const facilityId = await getItem('selectedFacilityId');

            if (!facilityId) {
                toastify('Facility ID not found', 'error');
                setLoading(false);
                return;
            }

            const serviceData = {
                serviceName,
                providerType
            }

            const response = await makeRequest2(`${addNewValueAddedService}/${facilityId}`, 'POST', serviceData);

            if (response.success) {
                toastify('Service added successfully', 'success');
                // Reset form
                setServiceName("");
                setProviderType("INTERNAL");
                // Switch to list tab to show the new service
                setActiveTab('list');
                fetchServices(); // Refresh the list
            } else {
                throw new Error(response.message || 'Failed to add service');
            }
        } catch (error) {
            toastify(error.message, 'error');
        } finally {
            setLoading(false);
        }
    };

    const handleChange = (e) => {
        const { name, value } = e.target;

        if (name === "serviceName") {
            setServiceName(value);
        } else if (name === "providerType") {
            setProviderType(value);
        }
    };

    // Helper function to format provider type for display
    const formatProviderType = (type) => {
        switch (type) {
            case 'INTERNAL': return 'Internal Only';
            case 'EXTERNAL': return 'External Vendors Only';
            case 'BOTH': return 'Internal & External';
            default: return type;
        }
    };

    return (
        <Layout>
            <div>
                <div className="row">
                    <div className="col-sm-12">
                        {/* Tabs - Using the exact styling from your example */}
                        <div className="card">
                            <div className="card-body py-0">
                                <ul className="nav nav-tabs profile-tabs" id="myTab" role="tablist">
                                    <li className="nav-item">
                                        <button
                                            type="button"
                                            className={`nav-link cursor-pointer ${activeTab === 'add' ? 'active' : ''}`}
                                            onClick={() => handleTabClick('add')}
                                            role="tab"
                                            aria-selected={activeTab === 'add'}
                                        >
                                            <i className="ti ti-plus me-2"></i>Add Service
                                        </button>
                                    </li>
                                    <li className="nav-item">
                                        <button
                                            type="button"
                                            className={`nav-link cursor-pointer ${activeTab === 'list' ? 'active' : ''}`}
                                            onClick={() => handleTabClick('list')}
                                            role="tab"
                                            aria-selected={activeTab === 'list'}
                                        >
                                            <i className="ti ti-list me-2"></i>View Services
                                        </button>
                                    </li>
                                </ul>
                            </div>
                        </div>

                        {/* Tab Content */}
                        <div className="tab-content">
                            {/* Add Service Tab */}
                            {activeTab === 'add' && (
                                <div className="tab-pane show active" role="tabpanel">
                                    <div className="card">
                                        <div className="card-header">
                                            <h5>Add New Service</h5>
                                        </div>
                                        <div className='card-body'>
                                            <div className="row">
                                                <div className="col-lg-12 col-xxl-12">
                                                    <div className="card">
                                                        <div className="card-body position-relative">
                                                            <form onSubmit={handleAddService}>

                                                                {/* BASIC INFO */}
                                                                <h6 className="fw-bold mb-3 text-primary">Service Information</h6>

                                                                <div className="row">
                                                                    <div className="col-md-6 mb-3">
                                                                        <label className="form-label fw-semibold">Service Name *</label>
                                                                        <input
                                                                            type="text"
                                                                            className="form-control"
                                                                            name="serviceName"
                                                                            value={serviceName}
                                                                            onChange={handleChange}
                                                                            placeholder="e.g. House Keeping"
                                                                            disabled={loading}
                                                                        />
                                                                    </div>
                                                                </div>

                                                                <hr />

                                                                {/* PROVIDER TYPE */}
                                                                <h6 className="fw-bold mb-3 text-primary">Provider Configuration</h6>

                                                                <div className="row">
                                                                    <div className="col-md-6 mb-3">
                                                                        <label className="form-label fw-semibold">Provider Type</label>
                                                                        <select
                                                                            className="form-select"
                                                                            name="providerType"
                                                                            value={providerType}
                                                                            onChange={handleChange}
                                                                            disabled={loading}
                                                                        >
                                                                            <option value="INTERNAL">Internal Only</option>
                                                                            <option value="EXTERNAL">External Vendors Only</option>
                                                                            <option value="BOTH">Internal & External</option>
                                                                        </select>
                                                                    </div>
                                                                </div>

                                                                <hr />

                                                                {/* ACTIONS */}
                                                                <div className="d-flex justify-content-end">
                                                                    <button
                                                                        type="button"
                                                                        className="btn btn-light me-2"
                                                                        onClick={() => navigate(-1)}
                                                                        disabled={loading}
                                                                    >
                                                                        Cancel
                                                                    </button>

                                                                    <button
                                                                        type="submit"
                                                                        className="btn btn-primary px-4"
                                                                        disabled={loading}
                                                                    >
                                                                        {loading ? (
                                                                            <>
                                                                                <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                                                                                Saving...
                                                                            </>
                                                                        ) : (
                                                                            'Save Service'
                                                                        )}
                                                                    </button>
                                                                </div>

                                                            </form>
                                                        </div>
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            )}

                            {/* View Services Tab */}
                            {activeTab === 'list' && (
                                <div className="tab-pane show active" role="tabpanel">
                                    <div className="card">
                                        <div className="card-header">
                                            <h5>Services List</h5>
                                        </div>
                                        <div className='card-body'>
                                            {fetchingServices ? (
                                                <div className="text-center py-5">
                                                    <div className="spinner-border text-primary" role="status">
                                                        <span className="visually-hidden">Loading...</span>
                                                    </div>
                                                    <p className="mt-2">Loading services...</p>
                                                </div>
                                            ) : services.length > 0 ? (
                                                <div className="table-responsive">
                                                    <table className="table table-hover">
                                                        <thead>
                                                            <tr>
                                                                <th>#</th>
                                                                <th>Service Name</th>
                                                                <th>Provider Type</th>
                                                                <th>Actions</th>
                                                            </tr>
                                                        </thead>
                                                        <tbody>
                                                            {services.map((service, index) => (
                                                                <tr key={service._id || index}>
                                                                    <td>{index + 1}</td>
                                                                    <td>
                                                                        <strong>{service.serviceName}</strong>
                                                                    </td>
                                                                    <td>
                                                                        <span className="badge bg-info">
                                                                            {formatProviderType(service.providerType)}
                                                                        </span>
                                                                    </td>
                                                                    <td>
                                                                        <button
                                                                            className="btn btn-sm btn-outline-primary me-2"
                                                                            title="Edit"
                                                                            onClick={() => console.log('Edit service:', service._id)}
                                                                        >
                                                                            <i className="ti ti-edit"></i>
                                                                        </button>
                                                                        <button
                                                                            className="btn btn-sm btn-outline-info"
                                                                            title="View Details"
                                                                            onClick={() => console.log('View service:', service._id)}
                                                                        >
                                                                            <i className="ti ti-eye"></i>
                                                                        </button>
                                                                    </td>
                                                                </tr>
                                                            ))}
                                                        </tbody>
                                                    </table>
                                                </div>
                                            ) : (
                                                <div className="text-center py-5">
                                                    <i className="ti ti-file-text" style={{ fontSize: '48px', color: '#ccc' }}></i>
                                                    <h6 className="mt-3">No services found</h6>
                                                    <p className="text-muted">Get started by adding your first service</p>
                                                    <button
                                                        className="btn btn-primary"
                                                        onClick={() => setActiveTab('add')}
                                                    >
                                                        <i className="ti ti-plus me-2"></i>
                                                        Add Service
                                                    </button>
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            </div>
        </Layout>
    )
}

export default ServiceVendor